import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Users, Briefcase, FileText, Clock, Building2, Download, Loader2, Archive, Trash2, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { getCompanyDeletionPreview, softDeleteCompany, exportCompanyData, hardDeleteCompany, restoreCompany } from '../../../../services/companyService';
import { useAuth } from '../../../../contexts/AuthContext';

const DeletionPreviewModal = ({ company, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deletionReason, setDeletionReason] = useState('');
  const [actionSuccess, setActionSuccess] = useState(null);

  useEffect(() => {
    loadPreview();
  }, [company?.id]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCompanyDeletionPreview(company?.id);
      if (result?.success) {
        setPreview(result?.data);
      } else {
        setError(result?.error || 'Failed to load deletion preview');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'json') => {
    setExportLoading(true);
    try {
      const result = await exportCompanyData(company?.id, format);
      if (result?.success) {
        if (format === 'excel') {
          // Download single Excel file with multiple tabs
          const blob = new Blob([result.buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = result.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          // Download single JSON file
          const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = result.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } else {
        setError(result?.error || 'Failed to export data');
      }
    } catch (err) {
      setError(err?.message || 'Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleArchive = async () => {
    if (confirmText !== company?.companyTag) {
      setError('Please type the company tag to confirm');
      return;
    }
    if (!deletionReason?.trim()) {
      setError('Please provide a reason for archiving');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const result = await softDeleteCompany(company?.id, deletionReason, user?.id);
      if (result?.success) {
        setActionSuccess(result?.message);
        setTimeout(() => {
          onSuccess?.();
        }, 2000);
      } else {
        setError(result?.error || 'Failed to archive company');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (confirmText !== company?.companyTag) {
      setError('Please type the company tag to confirm');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const result = await hardDeleteCompany(company?.id);
      if (result?.success) {
        setActionSuccess(result?.message);
        setTimeout(() => {
          onSuccess?.();
        }, 2000);
      } else {
        setError(result?.error || 'Failed to delete company');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await restoreCompany(company?.id);
      if (result?.success) {
        setActionSuccess(result?.message);
        setTimeout(() => {
          onSuccess?.();
        }, 2000);
      } else {
        setError(result?.error || 'Failed to restore company');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const isConfirmValid = confirmText === company?.companyTag;
  const isArchived = preview?.company?.isDeleted;
  const canPermanentlyDelete = isArchived && preview?.company?.deletionScheduledAt && new Date() >= new Date(preview?.company?.deletionScheduledAt);

  const getDaysUntilPermanentDelete = () => {
    if (!preview?.company?.deletionScheduledAt) return null;
    const scheduledDate = new Date(preview?.company?.deletionScheduledAt);
    const now = new Date();
    const diff = scheduledDate - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysUntilPermanentDelete();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                {isArchived ? 'Archived Company' : 'Delete Company'}
              </h2>
              <p className="text-text-secondary text-sm">{company?.companyName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface rounded-lg transition-colors">
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Success Message */}
          {actionSuccess && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <p className="text-green-400 font-medium">{actionSuccess}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-orange-400 animate-spin mb-4" />
              <p className="text-text-secondary">Loading deletion preview...</p>
            </div>
          ) : preview && !actionSuccess ? (
            <>
              {/* Archived Status Banner */}
              {isArchived && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Archive className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium">This company is archived</p>
                      {daysRemaining !== null && daysRemaining > 0 ? (
                        <p className="text-text-secondary text-sm mt-1">
                          Permanent deletion available in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}.
                          You can restore it at any time before then.
                        </p>
                      ) : (
                        <p className="text-text-secondary text-sm mt-1">
                          Grace period has elapsed. You can now permanently delete this company.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Warning Banner */}
              {!isArchived && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-medium">This action has significant impact</p>
                      <p className="text-text-secondary text-sm mt-1">
                        Archiving will immediately block all users from accessing this company.
                        Data will be preserved for 30 days, after which permanent deletion becomes available.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Counts Grid */}
              <div>
                <h3 className="text-text-primary font-medium mb-3">Data that will be affected</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  <div className="bg-surface border border-border rounded-lg p-3 text-center">
                    <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-xl font-semibold text-text-primary">{preview?.counts?.users || 0}</p>
                    <p className="text-text-tertiary text-xs">Users</p>
                  </div>
                  <div className="bg-surface border border-border rounded-lg p-3 text-center">
                    <Briefcase className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                    <p className="text-xl font-semibold text-text-primary">{preview?.counts?.jobs || 0}</p>
                    <p className="text-text-tertiary text-xs">Jobs</p>
                  </div>
                  <div className="bg-surface border border-border rounded-lg p-3 text-center">
                    <Clock className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-xl font-semibold text-text-primary">{preview?.counts?.jobInstances || 0}</p>
                    <p className="text-text-tertiary text-xs">Job Instances</p>
                  </div>
                  <div className="bg-surface border border-border rounded-lg p-3 text-center">
                    <FileText className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                    <p className="text-xl font-semibold text-text-primary">{preview?.counts?.timesheets || 0}</p>
                    <p className="text-text-tertiary text-xs">Timesheets</p>
                  </div>
                  <div className="bg-surface border border-border rounded-lg p-3 text-center">
                    <Building2 className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                    <p className="text-xl font-semibold text-text-primary">{preview?.counts?.serviceReceivers || 0}</p>
                    <p className="text-text-tertiary text-xs">Clients</p>
                  </div>
                </div>
              </div>

              {/* Warnings */}
              {preview?.warnings?.length > 0 && (
                <div>
                  <h3 className="text-text-primary font-medium mb-2">Warnings</h3>
                  <ul className="space-y-1">
                    {preview.warnings.map((warning, idx) => (
                      <li key={idx} className="text-yellow-400 text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Affected Users */}
              {preview?.users?.length > 0 && (
                <div>
                  <h3 className="text-text-primary font-medium mb-2">Affected Users ({preview.users.length})</h3>
                  <div className="bg-surface border border-border rounded-lg max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-surface">
                        <tr className="border-b border-border">
                          <th className="text-left p-2 text-text-secondary font-medium">Name</th>
                          <th className="text-left p-2 text-text-secondary font-medium">Email</th>
                          <th className="text-left p-2 text-text-secondary font-medium">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.users.map((u, idx) => (
                          <tr key={idx} className="border-b border-border/50 last:border-0">
                            <td className="p-2 text-text-primary">{u?.name || 'N/A'}</td>
                            <td className="p-2 text-text-secondary">{u?.email || 'N/A'}</td>
                            <td className="p-2 text-text-tertiary capitalize">{u?.role?.replace('_', ' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-text-tertiary text-xs mt-2">
                    These users will lose access to this company but their Mervo accounts will remain active.
                  </p>
                </div>
              )}

              {/* Export Data Section */}
              <div className="bg-surface border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-text-primary font-medium">Export Company Data</p>
                    <p className="text-text-secondary text-sm">Download all data before deletion</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleExport('excel')}
                    disabled={exportLoading}
                    className="flex-1 px-4 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {exportLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-4 h-4" />
                        Export as Excel
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleExport('json')}
                    disabled={exportLoading}
                    className="flex-1 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {exportLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Export as JSON
                      </>
                    )}
                  </button>
                </div>
                <p className="text-text-tertiary text-xs mt-2">
                  Excel exports a single file with all data in separate tabs. JSON includes all data in one file.
                </p>
              </div>

              {/* Confirmation Section */}
              {!isArchived && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-text-primary text-sm font-medium mb-2">
                      Reason for archiving <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={deletionReason}
                      onChange={(e) => setDeletionReason(e.target.value)}
                      placeholder="Provide a reason for archiving this company..."
                      rows={2}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-text-primary text-sm font-medium mb-2">
                      Type <span className="font-mono text-orange-400">{company?.companyTag}</span> to confirm
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="Enter company tag..."
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                    />
                  </div>
                </div>
              )}

              {/* Archived Company Actions */}
              {isArchived && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-text-primary text-sm font-medium mb-2">
                      Type <span className="font-mono text-orange-400">{company?.companyTag}</span> to confirm permanent deletion
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="Enter company tag..."
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                    />
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer Actions */}
        {!loading && !actionSuccess && (
          <div className="border-t border-border p-6">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={actionLoading}
                className="flex-1 px-4 py-2.5 bg-surface hover:bg-surface-light text-text-primary rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>

              {isArchived ? (
                <>
                  <button
                    onClick={handleRestore}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Restore Company
                  </button>
                  <button
                    onClick={handlePermanentDelete}
                    disabled={actionLoading || !isConfirmValid || !canPermanentlyDelete}
                    className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 disabled:bg-red-500/50 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    title={!canPermanentlyDelete ? `Available in ${daysRemaining} days` : ''}
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delete Permanently
                  </button>
                </>
              ) : (
                <button
                  onClick={handleArchive}
                  disabled={actionLoading || !isConfirmValid || !deletionReason?.trim()}
                  className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4" />
                      Archive Company
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletionPreviewModal;
